/**
 * HVAC Export Utilities
 * Functions for exporting HVAC data to CSV and JSON formats
 */

import JSZip from 'jszip';

/**
 * Generate CSV for room data
 */
export function generateRoomDataCSV(rooms, roomData) {
  const headers = [
    'Room Name',
    'Room Type',
    'Conditioned',
    'Floor Area (sqft)',
    'Ceiling Height (ft)',
    'Ceiling Type',
    'Window Count',
    'Total Window Area (sqft)',
    'Window Types',
    'Exterior Wall Area (sqft)',
    'Wall Construction',
    'Wall Insulation',
    'Wall Directions',
    'Floor Type',
    'Floor Insulation',
    'Exterior Door Count',
    'Notes',
  ];

  const rows = rooms.map(room => {
    const data = roomData?.find(rd => rd.roomId === room.id) || {};

    // Calculate window area
    const windows = data.windows || [];
    const windowArea = windows.reduce((sum, w) => {
      return sum + ((w.width || 36) * (w.height || 48)) / 144; // Convert to sqft
    }, 0);
    const windowTypes = [...new Set(windows.map(w => w.type || 'unknown'))].join(', ');

    // Calculate wall area
    const walls = data.exteriorWalls || [];
    const wallArea = walls.reduce((sum, w) => {
      return sum + (w.length || 0) * (data.ceilingHeight || 8);
    }, 0);
    const wallConstruction = [...new Set(walls.map(w => w.construction || 'unknown'))].join(', ');
    const wallInsulation = [...new Set(walls.map(w => w.insulation || 'unknown'))].join(', ');
    const wallDirections = [...new Set(walls.map(w => w.facing || 'unknown'))].join(', ');

    // Door count
    const doorCount = (data.doors || []).length;

    return [
      room.name || 'Unnamed Room',
      data.name || 'bedroom',
      data.conditioned || 'yes',
      data.floorArea || room.area || 0,
      data.ceilingHeight || 8,
      data.ceilingType || 'below-vented-attic',
      windows.length,
      Math.round(windowArea * 10) / 10,
      windowTypes || 'none',
      Math.round(wallArea),
      wallConstruction || 'none',
      wallInsulation || 'none',
      wallDirections || 'none',
      data.floorType || 'slab',
      data.floorInsulation || 'none',
      doorCount,
      (data.notes || '').replace(/,/g, ';').replace(/\n/g, ' '),
    ];
  });

  return [headers, ...rows].map(row => row.map(escapeCSV).join(',')).join('\n');
}

/**
 * Generate CSV for equipment data
 */
export function generateEquipmentCSV(equipment) {
  const headers = ['Equipment Type', 'Size', 'Location Description'];

  const rows = (equipment || []).map(eq => {
    // Generate location description from coordinates
    const locationDesc = eq.roomId
      ? `Room ID: ${eq.roomId}`
      : `Position: (${Math.round(eq.x / 40)}'', ${Math.round(eq.y / 40)}'')`;

    return [
      eq.type || 'unknown',
      eq.size || 'standard',
      locationDesc,
    ];
  });

  return [headers, ...rows].map(row => row.map(escapeCSV).join(',')).join('\n');
}

/**
 * Generate CSV for ductwork data
 */
export function generateDuctworkCSV(ducts) {
  const headers = ['Duct Type', 'Size', 'Length (ft)', 'Material', 'Insulated'];

  const rows = (ducts || []).map(duct => {
    // Calculate length from points
    let length = 0;
    if (duct.points && duct.points.length >= 2) {
      for (let i = 0; i < duct.points.length - 1; i++) {
        const dx = duct.points[i + 1].x - duct.points[i].x;
        const dy = duct.points[i + 1].y - duct.points[i].y;
        length += Math.sqrt(dx * dx + dy * dy) / 40; // Convert pixels to feet
      }
    }

    return [
      duct.type || 'supply',
      duct.size || 6,
      Math.round(length * 10) / 10,
      duct.shape === 'flex' ? 'Flex Duct' : 'Sheet Metal',
      duct.insulated ? 'Yes' : 'No',
    ];
  });

  return [headers, ...rows].map(row => row.map(escapeCSV).join(',')).join('\n');
}

/**
 * Escape CSV values
 */
function escapeCSV(value) {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Generate full JSON export
 */
export function generateHVACJSON(activeFloor, projectInfo = {}) {
  const rooms = activeFloor?.rooms || [];
  const roomData = activeFloor?.roomData || [];
  const equipment = activeFloor?.equipment || [];
  const ducts = activeFloor?.ducts || [];
  const vents = activeFloor?.vents || [];
  const fittings = activeFloor?.fittings || [];

  // Calculate summary totals
  const totalSqft = rooms.reduce((sum, room) => {
    const data = roomData.find(rd => rd.roomId === room.id);
    return sum + (data?.floorArea || room.area || 0);
  }, 0);

  const totalSupplyCfm = vents
    .filter(v => v.type?.startsWith('supply'))
    .reduce((sum, v) => sum + (v.cfm || 0), 0);

  const totalReturnCfm = vents
    .filter(v => v.type?.startsWith('return'))
    .reduce((sum, v) => sum + (v.cfm || 0), 0);

  // Calculate total duct lengths
  const calculateDuctLength = (duct) => {
    if (!duct.points || duct.points.length < 2) return 0;
    let length = 0;
    for (let i = 0; i < duct.points.length - 1; i++) {
      const dx = duct.points[i + 1].x - duct.points[i].x;
      const dy = duct.points[i + 1].y - duct.points[i].y;
      length += Math.sqrt(dx * dx + dy * dy) / 40;
    }
    return length;
  };

  const totalSupplyDuctFeet = ducts
    .filter(d => d.type === 'supply')
    .reduce((sum, d) => sum + calculateDuctLength(d), 0);

  const totalReturnDuctFeet = ducts
    .filter(d => d.type === 'return')
    .reduce((sum, d) => sum + calculateDuctLength(d), 0);

  return {
    exportVersion: '1.0',
    exportDate: new Date().toISOString(),
    projectInfo: {
      name: projectInfo.projectName || 'Untitled Project',
      address: projectInfo.address || '',
      date: new Date().toLocaleDateString(),
      contractor: projectInfo.drawnBy || '',
      clientName: projectInfo.clientName || '',
    },
    rooms: rooms.map(room => {
      const data = roomData.find(rd => rd.roomId === room.id) || {};
      return {
        id: room.id,
        name: room.name || 'Unnamed Room',
        type: data.name || 'bedroom',
        conditioned: data.conditioned || 'yes',
        floorArea: data.floorArea || room.area || 0,
        ceilingHeight: data.ceilingHeight || 8,
        ceilingType: data.ceilingType || 'below-vented-attic',
        windows: data.windows || [],
        exteriorWalls: data.exteriorWalls || [],
        floorType: data.floorType || 'slab',
        floorInsulation: data.floorInsulation || 'none',
        doors: data.doors || [],
        notes: data.notes || '',
        // Include geometry for reference
        points: room.points || room.vertices || [],
      };
    }),
    equipment: equipment.map(eq => ({
      id: eq.id,
      type: eq.type,
      size: eq.size,
      rotation: eq.rotation || 0,
      position: { x: eq.x, y: eq.y },
      positionFeet: { x: Math.round(eq.x / 40 * 10) / 10, y: Math.round(eq.y / 40 * 10) / 10 },
    })),
    ducts: ducts.map(duct => ({
      id: duct.id,
      type: duct.type,
      shape: duct.shape,
      size: duct.size,
      insulated: duct.insulated || false,
      points: duct.points,
      lengthFeet: Math.round(calculateDuctLength(duct) * 10) / 10,
    })),
    vents: vents.map(vent => ({
      id: vent.id,
      type: vent.type,
      size: vent.size,
      cfm: vent.cfm || 0,
      rotation: vent.rotation || 0,
      position: { x: vent.x, y: vent.y },
      positionFeet: { x: Math.round(vent.x / 40 * 10) / 10, y: Math.round(vent.y / 40 * 10) / 10 },
      roomId: vent.roomId || null,
    })),
    fittings: fittings.map(fitting => ({
      id: fitting.id,
      type: fitting.type,
      ductType: fitting.ductType,
      size: fitting.size,
      rotation: fitting.rotation || 0,
      position: { x: fitting.x, y: fitting.y },
    })),
    summary: {
      totalConditionedArea: totalSqft,
      roomCount: rooms.length,
      equipmentCount: equipment.length,
      supplyVentCount: vents.filter(v => v.type?.startsWith('supply')).length,
      returnVentCount: vents.filter(v => v.type?.startsWith('return')).length,
      totalSupplyCfm,
      totalReturnCfm,
      totalSupplyDuctFeet: Math.round(totalSupplyDuctFeet * 10) / 10,
      totalReturnDuctFeet: Math.round(totalReturnDuctFeet * 10) / 10,
      totalDuctFeet: Math.round((totalSupplyDuctFeet + totalReturnDuctFeet) * 10) / 10,
      fittingCount: fittings.length,
    },
  };
}

/**
 * Download CSV files as a ZIP
 */
export async function downloadHVACCSVs(activeFloor, projectInfo = {}) {
  const zip = new JSZip();

  const rooms = activeFloor?.rooms || [];
  const roomData = activeFloor?.roomData || [];
  const equipment = activeFloor?.equipment || [];
  const ducts = activeFloor?.ducts || [];

  // Generate CSVs
  const roomCSV = generateRoomDataCSV(rooms, roomData);
  const equipmentCSV = generateEquipmentCSV(equipment);
  const ductCSV = generateDuctworkCSV(ducts);

  // Add to zip
  zip.file('room_data.csv', roomCSV);
  zip.file('equipment.csv', equipmentCSV);
  zip.file('ductwork.csv', ductCSV);

  // Generate and download
  const blob = await zip.generateAsync({ type: 'blob' });
  const filename = `hvac_export_${(projectInfo.projectName || 'project').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.zip`;

  downloadBlob(blob, filename);
}

/**
 * Download JSON file
 */
export function downloadHVACJSON(activeFloor, projectInfo = {}) {
  const data = generateHVACJSON(activeFloor, projectInfo);
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const filename = `hvac_data_${(projectInfo.projectName || 'project').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;

  downloadBlob(blob, filename);
}

/**
 * Helper to download a blob
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
